import React, {useState} from 'react';
import AppBar from "../components/app_bar/AppBar";
import Drawer from "../components/Drawer";
import { 
    Typography, CssBaseline, Box, Toolbar, Grid, Card, Stack, useMediaQuery,
    IconButton, InputAdornment, styled, useTheme, FormLabel, TextField, 
    Button,
} from '@mui/material';
import { useParams, useSearchParams } from "react-router-dom";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { reset_password } from '../services/login_system';

import {Visibility, VisibilityOff} from '@mui/icons-material'
import { checkPasswordComplexity } from 'check-password-complexity';

import * as CONSTANTS from "../constants"

const getColors = (theme) => [
    {
        color: theme.palette.error.main,
        value: 'tooWeak',
    },
    {
        color: theme.palette.warning.main,
        value: 'weak',
    },
    {
        color: theme.palette.success.light,
        value: 'medium',
    },
    {
        color: theme.palette.success.main,
        value: 'strong',
    },
];

const getPasswordStrengthResult = (strength, theme, lang) => {

    // default options
    switch (strength) {
        case 'tooWeak':
            return {
                label: CONSTANTS.SIGN_UP_FORM.PASSWORD_STRENGTH.TOO_WEAK[lang],
                color: theme?.palette.error.main
            };
        case 'weak':
            return {
                label: CONSTANTS.SIGN_UP_FORM.PASSWORD_STRENGTH.WEAK[lang],
                color: theme?.palette.warning.main
            };
        case 'medium':
            return {
                label: CONSTANTS.SIGN_UP_FORM.PASSWORD_STRENGTH.OKAY[lang],
                color: theme?.palette.success.light
            };
        default:
            return {
                label: CONSTANTS.SIGN_UP_FORM.PASSWORD_STRENGTH.STRONG[lang],
                color: theme?.palette.success.dark
            };
    }
}


const Bar = styled('div', {
    // Configure which props should be forwarded on DOM
    shouldForwardProp: (prop) =>
        prop !== 'color' && prop !== 'inactive' && prop !== 'inactiveColor' && prop !== 'barWidth',
})(({ theme, color, inactive, inactiveColor, barWidth }) => {
    const defaultInactiveColor = inactiveColor || theme.palette.grey[300];

    const style = {
        height: 6,
        borderRadius: 6,
        backgroundColor: inactive ? defaultInactiveColor : color,
    };

    if (barWidth) {
        style.width = barWidth;
    } else {
        style.flex = 1;
    }

    return style;
});


const ResetPassword = () => {

    document.title = `Reset Password - Wotagei Zone`

    let {lang} = useParams();

    const iconStyle = {
        transition: 'transform 0.5s, opacity 0.5s',
        display: 'inline-block',
    };

    const [searchParams, setSearchParams] = useSearchParams();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [passwordError, setPasswordError] = useState(false);
    const [passwordErrorMessage, setPasswordErrorMessage] = useState('');

    const [showPassword, setShowPassword] = useState(false);
    const handleShowPassword = () => setShowPassword(!showPassword);

     const [strengthOption, setStrengthOption] = useState(null);
    const handlePasswordChange = (event) => {
        const { value } = event.target;
        const result = checkPasswordComplexity(value);
        setStrengthOption(result);
    };

    const handleDrawerOpen = (open) => {
        if (open === undefined) {
            setDrawerOpen(!drawerOpen)
        } else {
            setDrawerOpen(open);
        }
    }

    const [message, setMessage] = useState('');
    const [userId, setUserId] = useState(searchParams.get('id'));
    const [userToken, setUserToken] = useState(searchParams.get('token'));
    const [password, setPassword] = useState('');
    const [resetStatus, setResetStatus] = useState('0');

    let isValidInput = false;

    const handleSubmit = async (e) => {
    
        e.preventDefault();

        if (!isValidInput){
            return;
        }
    
        const [status, result] = await reset_password(
            userId,
            userToken,
            password
        );

        // success reset
        if (status === 200) {
            setResetStatus('1');
        } 
        // failed reset
        else {
            setResetStatus('2');
        }
    };

    const validateInputs = () => {

        const user_password = document.getElementById('password') ;

        let isValid = true;

        if (!user_password.value || (strengthOption.value == "weak" || strengthOption.value == "tooWeak" )) {
            setPasswordError(true);
            setPasswordErrorMessage(CONSTANTS.LOGIN_FORM.ERROR_MESSAGE.PASSWORD_STRENGTH[lang]);
            isValid = false;
        } else {
            setPasswordError(false);
            setPasswordErrorMessage('');
        }

        setPassword(user_password.value);

        isValidInput = isValid;

        return isValid;

    }
   
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));  // Mobile detection

    return (
        <Box >
            <CssBaseline />
            <AppBar lang={lang} handleDrawerOpen={handleDrawerOpen}/>
            <Box sx={{ display: 'flex' }}>
                <Drawer lang={lang} drawerOpen={drawerOpen} setDrawerOpen={setDrawerOpen} />
                <Box component="main" sx={{ flexGrow: 1, p: isMobile? 2: 3}} >
                    <Toolbar />
                    <Grid container spacing={2} style={{ height: '80vh' }}>
                        <Grid item xs={12} alignContent={'center'} justifyItems={'center'} >
                            <Card
                                sx={{
                                    width: '30vw',
                                    padding: '3vw',
                                    minWidth: "300px",
                                }}
                            >
                                {{
                                    '0': <>
                                        <div>
                                            <Typography
                                                component="h1"
                                                variant="h4"
                                                fontWeight='bold'
                                                sx={{ width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)' }}
                                                mb={3}
                                            >
                                                {CONSTANTS.RESET_PASSWORD_FORM.RESET_PASSWORD[lang]}
                                            </Typography>
                                        </div>
                                        <Box
                                            component="form"
                                            onSubmit={handleSubmit}
                                            noValidate
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                width: '100%',
                                                gap: 2,
                                            }}
                                        >
                                            <Grid container spacing={2}>
                                                <Grid item xs={12}>
                                                    <FormLabel htmlFor="email">{CONSTANTS.LOGIN_FORM.PASSWORD[lang]+"*"}</FormLabel>
                                                    <div>
                                                        <TextField
                                                            error={passwordError}
                                                            helperText={passwordErrorMessage}
                                                            name="password"
                                                            placeholder="••••••"
                                                            type={showPassword ? "text" : "password"}
                                                            id="password"
                                                            autoComplete="current-password"
                                                            autoFocus
                                                            required
                                                            fullWidth
                                                            variant="outlined"
                                                            color={passwordError ? 'error' : 'primary'}
                                                            onChange={handlePasswordChange}
                                                            InputProps={{ // <-- This is where the toggle button is added.
                                                                endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <IconButton
                                                                    aria-label="toggle password visibility"
                                                                    onClick={() => handleShowPassword()}
                                                                    >
                                                                    {showPassword ? <Visibility /> : <VisibilityOff />}
                                                                    </IconButton>
                                                                </InputAdornment>
                                                                )
                                                            }}
                                                        />
                                                        {strengthOption && (
                                                            <Box sx={{ mt: 0.8 }}>
                                                            <Stack direction="row" spacing={3} alignItems="center">
                                                                {/*
                                                                * each strength level (4) will have a different color
                                                                * it will be displayed as a bar
                                                                */}
                                                                <Stack direction="row" spacing={1} flex={1}>
                                                                {getColors(theme).map((option, index) => (
                                                                    <Bar
                                                                    color={option.color}
                                                                    // the bar color depends on the strength level
                                                                    inactive={index + 1 > strengthOption?.score}
                                                                    />
                                                                    ))}
                                                                </Stack>
                                                                {/* label to be displayed depending of the strength level */}
                                                                <Typography
                                                                variant="caption"
                                                                sx={{ color: getPasswordStrengthResult(strengthOption?.value, theme, lang).color }}
                                                                >
                                                                {getPasswordStrengthResult(strengthOption?.value, theme, lang).label}
                                                                </Typography>
                                                            </Stack>
                                                            </Box>
                                                        )}
                                                    </div>
                                                </Grid>
                                            </Grid>
                                            <Button
                                                type="submit"
                                                fullWidth
                                                variant="contained"
                                                tabIndex="4"
                                                onClick={() => validateInputs()}
                                            >
                                                {CONSTANTS.RESET_PASSWORD_FORM.RESET_PASSWORD[lang]}
                                            </Button>
                                        </Box>
                                    </>,
                                    '1': <>
                                        <CheckCircleIcon
                                            style={{
                                                ...iconStyle,
                                                transform: 'scale(1.2)',
                                            }}
                                            color="success"
                                        />
                                        <Typography variant="h6" fontWeight={'bold'}>{CONSTANTS.RESET_PASSWORD.MESSAGE.RESET_SUCCESSFUL[lang]}</Typography>
                                        <Typography>{CONSTANTS.RESET_PASSWORD.MESSAGE.PASSWORD_RESET_SUCCESSFUL[lang]}</Typography>
                                    </>,
                                    '2': <>
                                        <CancelIcon
                                            style={{
                                                ...iconStyle,
                                                transform: 'scale(1.2)',
                                            }}
                                            color="error"
                                        />
                                        <Typography variant="h6" fontWeight={'bold'}>{CONSTANTS.RESET_PASSWORD.MESSAGE.RESET_FAILED[lang]}</Typography>
                                        <Typography>{CONSTANTS.RESET_PASSWORD.MESSAGE.SOMETHING_WENT_WRONG[lang]}</Typography>
                                    </>
                                }[resetStatus]}
                            </Card>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
        </Box>
    );
};
 
export default ResetPassword;